import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";

export interface CartItem {
  id: string; // unique key: `${restaurantId}-${menuId || itemId}-${deliveryType}`
  restaurantId: string;
  restaurantName: string;
  dailyMenuId: string | null;
  menuItemId: string | null;
  itemName: string;
  quantity: number;
  unitPrice: number;
  deliveryType: "dine_in" | "delivery" | "takeaway";
  menuTypeName?: string;
  selectedChoices?: string[];
  restaurantDeliveryFee?: number;
  restaurantBoxPrice?: number;
  restaurantMinOrderAmount?: number;
  foodTypeCount?: number;
  passCommissionToCustomer?: boolean;
  commissionPercent?: number;
  commissionPerOrder?: number;
  commissionVatApplicable?: boolean;
  commissionVatRate?: number;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, "id">) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  updateDeliveryType: (id: string, deliveryType: "dine_in" | "delivery" | "takeaway") => void;
  clearCart: () => void;
  totalItems: number;
  totalAmount: number;
  getItemsByRestaurant: () => Map<string, { name: string; items: CartItem[] }>;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
}

export type PaymentMethod = "card" | "cash";

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  card: "Bankkártya",
  cash: "Készpénz",
};

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = "napihami_cart";

function generateCartItemId(item: Omit<CartItem, "id">): string {
  return `${item.restaurantId}-${item.dailyMenuId || ""}-${item.menuItemId || ""}-${item.deliveryType}`;
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((item: Omit<CartItem, "id">) => {
    const id = generateCartItemId(item);
    setItems(prev => {
      const existing = prev.find(i => i.id === id);
      if (existing) {
        return prev.map(i => i.id === id ? { ...i, quantity: i.quantity + item.quantity } : i);
      }
      return [...prev, { ...item, id }];
    });
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems(prev => prev.filter(i => i.id !== id));
  }, []);

  const updateQuantity = useCallback((id: string, quantity: number) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(i => i.id !== id));
      return;
    }
    setItems(prev => prev.map(i => i.id === id ? { ...i, quantity } : i));
  }, []);

  const updateDeliveryType = useCallback((id: string, deliveryType: "dine_in" | "delivery" | "takeaway") => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, deliveryType } : i));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalAmount = items.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0);

  const getItemsByRestaurant = useCallback(() => {
    const map = new Map<string, { name: string; items: CartItem[] }>();
    for (const item of items) {
      if (!map.has(item.restaurantId)) {
        map.set(item.restaurantId, { name: item.restaurantName, items: [] });
      }
      map.get(item.restaurantId)!.items.push(item);
    }
    return map;
  }, [items]);

  return (
    <CartContext.Provider value={{
      items, addItem, removeItem, updateQuantity, updateDeliveryType, clearCart,
      totalItems, totalAmount, getItemsByRestaurant,
      isCartOpen, setIsCartOpen,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within CartProvider");
  return context;
}
